// CSV取込時の「未知の科目・便種・列」検出（Issue #7）
//
// 背景：既存VBAは列の絶対位置指定のため、配送業者が新しい便種/列を追加すると
// 位置ズレでエラーになり「気づける」。一方このアプリは項目名（タイトル）判定のため、
// 未知の科目・便種が来ても黙って集計から欠落し、計上漏れ→未払いに直結しうる。
// ここで未知項目を明示的に検出し、取込画面でブロック＋可視化する（黙って通さない）。
//
// ★ 2026-06-08 本城さん確定ルールに合わせ、佐川は「便種コード」で線引きする方式に変更。
//    （単価は同じ便種でも複数あり〔商業貨物=110/183 等〕、便種コードの方が安定）

import type { SagawaRow, SagawaBenkindRule } from "../../types";
import { classifySagawaCode, DEFAULT_SAGAWA_BENKIND_RULES } from "../calc/sagawaRules";

// ── 佐川：便種コードによる線引き ──────────────────────────────
// 既知/未知は便種マッピング（Issue #32）で判定する。ルールがある便種コードは既知、無ければ未知。
// 本城さんが設定画面でルールを追加すれば、その便種は「既知」になり取込が通る。

export type UnknownSagawaItem = {
  benkindCode: string; // 未知の便種コード
  benkindNames: string[]; // この便種コードで出現した実績便種名
  tans: number[]; // 出現した単数単価
  rowCount: number; // 該当行数
  totalQty: number; // 実績個数の合計
  sampleDates: string[]; // 出現日サンプル（最大3件）
};

// 佐川：便種マッピングにルールが無い便種コードを未知として集計する
export function detectSagawaUnknown(
  rows: SagawaRow[],
  rules: SagawaBenkindRule[] = DEFAULT_SAGAWA_BENKIND_RULES
): UnknownSagawaItem[] {
  const map = new Map<string, UnknownSagawaItem>();
  for (const row of rows) {
    const code = row.benkindCode || "";
    if (classifySagawaCode(code, rules) !== null) continue; // ルールあり＝既知
    let e = map.get(code);
    if (!e) {
      e = { benkindCode: code, benkindNames: [], tans: [], rowCount: 0, totalQty: 0, sampleDates: [] };
      map.set(code, e);
    }
    e.rowCount++;
    e.totalQty += row.countQty;
    if (row.benkindName && !e.benkindNames.includes(row.benkindName)) e.benkindNames.push(row.benkindName);
    if (row.tan && !e.tans.includes(row.tan)) e.tans.push(row.tan);
    if (e.sampleDates.length < 3 && row.workDate && !e.sampleDates.includes(row.workDate)) {
      e.sampleDates.push(row.workDate);
    }
  }
  return [...map.values()].sort((a, b) => a.benkindCode.localeCompare(b.benkindCode));
}

// ── ヤマト：数量列の網羅性チェック ──────────────────────────
// ヤマトは便種ごとに固定列。新サービスは「修正後○○配達個数 / ○○配完冊数 / ○○集荷個数」等の
// 数量列グループが増える形で現れる。yamato.ts が読む既知列以外の数量列が来たら未知として弾く。
//   計上対象9列 ＋ 持戻(返品)2列（現行は計上しないが既知なので無視扱い）を網羅。
export const YAMATO_KNOWN_QTY_COLS = new Set<string>([
  // 配達（計上対象）
  "修正後宅急便配達個数",
  "修正後EAZY配達個数",
  "修正後ネコポス配完冊数",
  "修正後クロネコゆうメール配完冊数",
  "修正後家財宅急便配達個数",
  // 集荷（計上対象）
  "修正後宅急便集荷個数",
  "修正後ネコポス集荷冊数",
  "修正後クロネコゆうメール集荷冊数",
  "修正後家財宅急便集荷個数",
  // 持戻（返品）：現行は計上しない。既知なので未知扱いしない
  "修正後ネコポス持戻冊数",
  "修正後クロネコゆうメール持戻冊数",
]);

export type UnknownYamatoColumn = {
  column: string; // 未知の数量列名
};

// ヤマト：ヘッダーに「修正後〜個数/冊数」の数量列があり、かつ既知セットに無いものを未知とする
export function detectYamatoUnknownColumns(header: string[]): UnknownYamatoColumn[] {
  const out: UnknownYamatoColumn[] = [];
  for (const col of header) {
    if (!col) continue;
    const isQtyCol = col.startsWith("修正後") && (col.endsWith("個数") || col.endsWith("冊数"));
    if (!isQtyCol) continue;
    if (YAMATO_KNOWN_QTY_COLS.has(col)) continue;
    out.push({ column: col });
  }
  return out;
}

// 取込ブロックの集約結果（UI表示用）
export type UnknownReport = {
  carrier: "sagawa" | "yamato";
  fileName: string;
  sagawa?: UnknownSagawaItem[];
  yamato?: UnknownYamatoColumn[];
};

// 未知が1件でもあるか
export function hasUnknown(report: UnknownReport): boolean {
  return (report.sagawa?.length ?? 0) > 0 || (report.yamato?.length ?? 0) > 0;
}
